import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  ArrowRight,
  Calendar,
  Edit3,
  ExternalLink,
  FileText,
  Globe,
  Mail,
  Loader2,
  PenSquare,
  Search,
  Send,
} from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { publishBlogPost, useBlogCategories, useBlogPosts, useBlogSeries, useBlogSites, useBlogSubscribers, type BlogPost } from "@/hooks/use-blog";

function formatDate(value: string | null): string {
  if (!value) return "Not published";
  return new Date(value).toLocaleDateString();
}

function statusTone(status: BlogPost["status"]): string {
  switch (status) {
    case "published":
      return "bg-emerald-500/10 text-emerald-700";
    case "scheduled":
      return "bg-amber-500/10 text-amber-700";
    case "archived":
      return "bg-slate-500/10 text-slate-700";
    default:
      return "bg-blue-500/10 text-blue-700";
  }
}

export default function BlogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPublishingId, setIsPublishingId] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const debouncedSearch = useDebounce(search, 300);

  const { sites, isLoading: isLoadingSites } = useBlogSites();
  const selectedSiteId = searchParams.get("siteId") ?? sites[0]?.id ?? "";
  const selectedStatus = searchParams.get("status") as BlogPost["status"] | null;
  const selectedCategoryId = searchParams.get("categoryId") ?? "";
  const page = Number(searchParams.get("page") ?? "1");

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === selectedSiteId) ?? null,
    [selectedSiteId, sites],
  );

  const { categories } = useBlogCategories(selectedSiteId || undefined);
  const { series } = useBlogSeries(selectedSiteId || undefined);
  const { subscribers, isLoading: isLoadingSubscribers } = useBlogSubscribers(selectedSiteId || undefined);
  const { posts, total, isLoading, refetch, pageSize } = useBlogPosts(selectedSiteId || undefined, {
    categoryId: selectedCategoryId || undefined,
    search: debouncedSearch || undefined,
    status: selectedStatus ?? undefined,
    page,
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const subscriberPreferenceSummary = useMemo(() => {
    const targeted = subscribers.filter((subscriber) => subscriber.categoryPreferences.length > 0).length;
    const broad = subscribers.length - targeted;
    return { targeted, broad };
  }, [subscribers]);
  const seriesById = useMemo(
    () => new Map(series.map((entry) => [entry.id, entry])),
    [series],
  );
  const groupedPosts = useMemo(() => {
    const grouped = new Map<string, BlogPost[]>();
    const standalone: BlogPost[] = [];

    for (const post of posts) {
      if (!post.seriesId) {
        standalone.push(post);
        continue;
      }

      const current = grouped.get(post.seriesId) ?? [];
      current.push(post);
      grouped.set(post.seriesId, current);
    }

    return {
      standalone,
      series: Array.from(grouped.entries()).map(([seriesId, items]) => ({
        seriesId,
        seriesTitle: seriesById.get(seriesId)?.title ?? "Untitled series",
        items: [...items].sort((left, right) => (left.seriesOrder ?? 999) - (right.seriesOrder ?? 999)),
      })),
    };
  }, [posts, seriesById]);

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
        return;
      }

      next.set(key, value);
    });

    if (!next.get("page")) {
      next.set("page", "1");
    }

    setSearchParams(next);
  };

  const handlePublish = async (post: BlogPost) => {
    if (!selectedSiteId) return;

    setIsPublishingId(post.id);
    setPublishError(null);

    try {
      await publishBlogPost(selectedSiteId, post.id);
      await refetch();
    } catch {
      setPublishError(`Failed to publish "${post.title}".`);
    } finally {
      setIsPublishingId(null);
    }
  };

  if (isLoadingSites) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center">
        <Globe className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/50" />
        <h1 className="mt-4 text-xl font-semibold text-[var(--color-foreground)]">Create a site before managing blog posts</h1>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          The mounted blog APIs are site-scoped, so this workspace stays honest until a real site container exists.
        </p>
        <Link
          to="/pages/sites"
          className="mt-5 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ background: "var(--gradient-energy)" }}
        >
          Open Sites Workspace <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            Content Engine
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">Blog management</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Metadata, page-builder body editing, publish state, and site wiring are managed here.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to={`/pages/sites${selectedSiteId ? `?siteId=${selectedSiteId}` : ""}`}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
          >
            <Globe className="h-4 w-4" />
            Site workspace
          </Link>
          <Link
            to={`/cms/blog/new?siteId=${selectedSiteId}`}
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
            style={{ background: "var(--gradient-energy)" }}
          >
            <FileText className="h-4 w-4" />
            New post
          </Link>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 lg:grid-cols-[180px_180px_200px_1fr]">
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Site</span>
          <select
            value={selectedSiteId}
            onChange={(event) => updateSearchParams({ siteId: event.target.value, page: "1" })}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Status</span>
          <select
            value={selectedStatus ?? ""}
            onChange={(event) => updateSearchParams({ status: event.target.value || null, page: "1" })}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
            <option value="archived">Archived</option>
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Category</span>
          <select
            value={selectedCategoryId}
            onChange={(event) => updateSearchParams({ categoryId: event.target.value || null, page: "1" })}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-muted-foreground)]">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                updateSearchParams({ q: event.target.value || null, page: "1" });
              }}
              placeholder="Search titles and excerpts"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] py-2 pl-10 pr-3 text-sm text-[var(--color-foreground)]"
            />
          </div>
        </label>
      </div>

      {selectedSite && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
            Managing posts for <span className="font-medium text-[var(--color-foreground)]">{selectedSite.name}</span>
            {" "}at <span className="font-mono text-xs">{selectedSite.slug}</span>.
          </div>

          <section className="grid gap-3 lg:grid-cols-3" aria-label="Blog workspace boundaries">
            <article className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Live now</p>
              <p className="mt-2 text-sm text-[var(--color-foreground)]">
                Site-scoped post inventory, metadata edits, scheduling, and publish actions are mounted on the real blog contract.
              </p>
            </article>

            <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                Page-builder body
              </p>
              <p className="mt-2 text-sm text-[var(--color-foreground)]">
                Article sections, constrained layouts, and body revisions are edited through the mounted blog Puck editor after the post record exists.
              </p>
            </article>

            <article className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Publish fan-out</p>
              <p className="mt-2 text-sm text-[var(--color-foreground)]">
                Comment moderation lives in the article detail workspace. One publish now drives article artifacts, blog discovery/RSS, subscriber newsletter events, and social draft events from the mounted contract.
              </p>
            </article>
          </section>

          <section className="grid gap-3 lg:grid-cols-3" aria-label="Blog subscriber operations">
            <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Confirmed subscribers</p>
              <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
                {isLoadingSubscribers ? "…" : subscribers.length}
              </p>
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                Mounted from the live `/subscribers` contract for this site.
              </p>
            </article>

            <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Audience shape</p>
              <p className="mt-2 text-sm text-[var(--color-foreground)]">
                {subscriberPreferenceSummary.targeted} targeted
                {" · "}
                {subscriberPreferenceSummary.broad} broad
              </p>
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                Targeted subscribers chose category preferences; broad subscribers still receive general updates.
              </p>
            </article>

            <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Recent joins</p>
              {subscribers.length === 0 ? (
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                  No confirmed readers yet for this site.
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  {subscribers.slice(0, 3).map((subscriber) => (
                    <div key={subscriber.id} className="flex items-center justify-between gap-3 rounded-xl bg-[var(--color-muted)]/40 px-3 py-2 text-sm">
                      <span className="inline-flex items-center gap-2 text-[var(--color-foreground)]">
                        <Mail className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
                        subscriber-{subscriber.id.slice(0, 6)}
                      </span>
                      <span className="text-[var(--color-muted-foreground)]">
                        {new Date(subscriber.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </section>
        </div>
      )}

      {publishError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-700">
          {publishError}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center">
          <PenSquare className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/50" />
          <h2 className="mt-4 text-lg font-semibold text-[var(--color-foreground)]">No posts match this filter</h2>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Create the first post for this site or loosen the current status and category filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedPosts.series.map((group) => (
            <section key={group.seriesId} className="space-y-3">
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                  Series
                </p>
                <h2 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{group.seriesTitle}</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Ordered posts on the mounted series seam.
                </p>
              </div>

              {group.items.map((post) => (
                <article
                  key={post.id}
                  className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusTone(post.status)}`}>
                          {post.status}
                        </span>
                        <span className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs text-[var(--color-primary)]">
                          Part {post.seriesOrder ?? "?"}
                        </span>
                        {(post.categories ?? []).slice(0, 3).map((category) => (
                          <span
                            key={category.id}
                            className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs text-[var(--color-primary)]"
                          >
                            {category.name}
                          </span>
                        ))}
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-[var(--color-foreground)]">{post.title}</h3>
                        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                          {post.excerpt || "No excerpt yet. Add metadata, then open the page editor to build the article body."}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-4 text-xs text-[var(--color-muted-foreground)]">
                        <span>Slug: {post.slug}</span>
                        <span>Author: {post.authorName}</span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {post.status === "scheduled" ? `Scheduled ${formatDate(post.scheduledFor)}` : formatDate(post.publishedAt)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <Link
                        to={`/cms/blog/${post.id}/edit?siteId=${post.siteId}`}
                        className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                      >
                        <Edit3 className="h-4 w-4" />
                        Metadata
                      </Link>
                      <Link
                        to={`/pages/editor/${post.pageId}?siteId=${post.siteId}&entry=blog`}
                        className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Body editor
                      </Link>
                      {post.status !== "published" && (
                        <button
                          type="button"
                          onClick={() => {
                            void handlePublish(post);
                          }}
                          disabled={isPublishingId === post.id}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                          style={{ background: "var(--gradient-energy)" }}
                        >
                          {isPublishingId === post.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          Publish
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </section>
          ))}

          {groupedPosts.standalone.map((post) => (
            <article
              key={post.id}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusTone(post.status)}`}>
                      {post.status}
                    </span>
                    {(post.categories ?? []).slice(0, 3).map((category) => (
                      <span
                        key={category.id}
                        className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs text-[var(--color-primary)]"
                      >
                        {category.name}
                      </span>
                    ))}
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{post.title}</h2>
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                      {post.excerpt || "No excerpt yet. Add metadata, then open the page editor to build the article body."}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-4 text-xs text-[var(--color-muted-foreground)]">
                    <span>Slug: {post.slug}</span>
                    <span>Author: {post.authorName}</span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {post.status === "scheduled" ? `Scheduled ${formatDate(post.scheduledFor)}` : formatDate(post.publishedAt)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <Link
                    to={`/cms/blog/${post.id}/edit?siteId=${post.siteId}`}
                    className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                  >
                    <Edit3 className="h-4 w-4" />
                    Metadata
                  </Link>
                  <Link
                    to={`/pages/editor/${post.pageId}?siteId=${post.siteId}&entry=blog`}
                    className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Body editor
                  </Link>
                  {post.status !== "published" && (
                    <button
                      type="button"
                      onClick={() => {
                        void handlePublish(post);
                      }}
                      disabled={isPublishingId === post.id}
                      className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                      style={{ background: "var(--gradient-energy)" }}
                    >
                      {isPublishingId === post.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Publish
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => updateSearchParams({ page: String(page - 1) })}
            className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-[var(--color-muted-foreground)]">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => updateSearchParams({ page: String(page + 1) })}
            className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
